// components/FacultyAttendanceModal.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Search, CheckCircle, AlertCircle, Loader2, Users, 
  UserCheck, UserX, Save, ChevronDown
} from 'lucide-react';
import api from '../../api';

const AttendanceModal = ({ isOpen, onClose, session, facultyId, policy, onSuccess }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [attendanceData, setAttendanceData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sessionDetails, setSessionDetails] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const sessionDetailsCacheRef = useRef(new Map());
  const lastFetchedSessionIdRef = useRef(null);

  const normalizeStudentId = useCallback((id) => {
    if (!id) return '';
    if (typeof id === 'string' || typeof id === 'number') return String(id);
    if (typeof id === 'object') {
      if (id.$oid) return String(id.$oid);
      if (id._id) return String(id._id);
      if (id.studentId) return String(id.studentId);
      if (id.id) return String(id.id);
      if (typeof id.toHexString === 'function') return String(id.toHexString());
      if (typeof id.toString === 'function' && id.toString() !== '[object Object]') {
        return String(id.toString());
      }
      return '';
    }
    return String(id);
  }, []);

  const resolveStudentKey = useCallback((student, fallback = '') => {
    if (!student) return fallback;
    return (
      normalizeStudentId(student.studentId) ||
      normalizeStudentId(student.studentDetails?.studentId) ||
      normalizeStudentId(student.studentDetails?._id) ||
      normalizeStudentId(student.studentDetails?.authUserId) ||
      fallback
    );
  }, [normalizeStudentId]);

  // Accept eligibility from either sessionStatus or top-level fields, with a safe fallback for unblocked sessions.
  const isMarkedSession = session?.attendanceMarkingStatus === 'completed';
  const isBlockedSession = session?.isLocked === true || session?.isBlocked === true;
  const canEditOrMark = (
    (session?.sessionStatus?.canEdit === true || session?.sessionStatus?.canMark === true) ||
    session?.canEdit === true || session?.canMark === true ||
    session?.isUnblockedByAdmin === true ||
    (!isMarkedSession && !isBlockedSession)
  );
  const isEditMode = session?.attendanceMarkingStatus === 'completed' && canEditOrMark;
  const isMarkMode = !isEditMode && session?.attendanceMarkingStatus !== 'completed' && canEditOrMark;
  const isViewOnly = !canEditOrMark;

  const fetchSessionDetails = useCallback(async () => {
    const sessionId = session?._id;
    if (!sessionId) return;
    const shouldUseCache = !isEditMode;

    // Ensure previous session banners never leak into a new session view.
    setSuccess('');
    setError('');

    const hasHydratedStudentDetails = (attendanceRecords = []) =>
      attendanceRecords.length > 0 && attendanceRecords.every(record => {
        const details = record?.studentDetails;
        return details && (details.name || details.USN || details.rollNumber || details.studentId);
      });

    if (
      shouldUseCache &&
      session?.attendance &&
      Array.isArray(session.attendance) &&
      hasHydratedStudentDetails(session.attendance)
    ) {
      sessionDetailsCacheRef.current.set(sessionId, session);
      setSessionDetails(session);
      const initialData = {};
      const studentsList = [];
      session.attendance.forEach((record, index) => {
        const key = resolveStudentKey(record, `row_${index}`);
        initialData[key] = {
          status: isMarkMode ? '' : (record.status || 'absent'),
          markedAt: record.markedAt
        };
        studentsList.push({ ...record, __attendanceKey: key });
      });
      setAttendanceData(initialData);
      setStudents(studentsList);
      setLoading(false);
      return;
    }

    if (shouldUseCache && sessionDetailsCacheRef.current.has(sessionId)) {
      const cached = sessionDetailsCacheRef.current.get(sessionId);
      if (
        !cached?.attendance ||
        !Array.isArray(cached.attendance) ||
        !hasHydratedStudentDetails(cached.attendance)
      ) {
        sessionDetailsCacheRef.current.delete(sessionId);
      } else {
      setSessionDetails(cached);
      const initialData = {};
      const studentsList = [];
      (cached.attendance || []).forEach((record, index) => {
        const key = resolveStudentKey(record, `row_${index}`);
        initialData[key] = {
          status: isMarkMode ? '' : (record.status || 'absent'),
          markedAt: record.markedAt
        };
        studentsList.push({ ...record, __attendanceKey: key });
      });
      setAttendanceData(initialData);
      setStudents(studentsList);
      setLoading(false);
      return;
      }
    }

    try {
      lastFetchedSessionIdRef.current = sessionId;
      setLoading(true);
      console.log('[AttendanceModal] Fetching session details for session:', sessionId);
      const response = await api.get(`/api/faculty/attendance/session/${sessionId}`);
      console.log('[AttendanceModal] API response:', response);
      if (response.data.success) {
        const data = response.data.data;
        if (shouldUseCache) {
          sessionDetailsCacheRef.current.set(sessionId, data.session);
        }
        setSessionDetails(data.session);
        console.log('[AttendanceModal] Session data:', data.session);
        if (!data.session.attendance || !Array.isArray(data.session.attendance)) {
          console.warn('[AttendanceModal] No attendance array found in session data:', data.session);
        }
        const initialData = {};
        const studentsList = [];
        data.session.attendance.forEach((record, index) => {
          const key = resolveStudentKey(record, `row_${index}`);
          initialData[key] = {
            status: isMarkMode ? '' : (record.status || 'absent'),
            markedAt: record.markedAt
          };
          studentsList.push({ ...record, __attendanceKey: key });
        });
        console.log('[AttendanceModal] Students fetched:', studentsList.length, studentsList.map(s => s.studentId));
        setAttendanceData(initialData);
        setStudents(studentsList);
      } else {
        console.error('[AttendanceModal] API error:', response.data.message);
        setError(response.data.message || 'Failed to load session details');
      }
    } catch (err) {
      console.error('[AttendanceModal] Error fetching session details:', err);
      setError(err.response?.data?.message || 'Failed to load session details');
    } finally {
      setLoading(false);
    }
  }, [isEditMode, isMarkMode, resolveStudentKey, session]);

  useEffect(() => {
    if (!isOpen) {
      lastFetchedSessionIdRef.current = null;
      setSuccess('');
      setError('');
      return;
    }

    if (isEditMode && session?._id) {
      fetchSessionDetails();
      return;
    }

    if (session?._id && lastFetchedSessionIdRef.current !== session._id) {
      fetchSessionDetails();
    }
  }, [isOpen, isEditMode, session?._id, fetchSessionDetails]);

  const handleStatusChange = (studentId, status) => {
    if (isViewOnly) return;
    const key = normalizeStudentId(studentId);
    setAttendanceData(prev => ({
      ...prev,
      [key]: { ...prev[key], status }
    }));
  };

  const handleMarkAllPresent = () => {
    if (isViewOnly) return;
    const newData = {};
    students.forEach((student, index) => {
      const key = student.__attendanceKey || resolveStudentKey(student, `row_${index}`);
      newData[key] = { status: 'present' };
    });
    setAttendanceData(newData);
    setShowBulkActions(false);
  };

  const handleMarkAllAbsent = () => {
    if (isViewOnly) return;
    const newData = {};
    students.forEach((student, index) => {
      const key = student.__attendanceKey || resolveStudentKey(student, `row_${index}`);
      newData[key] = { status: 'absent' };
    });
    setAttendanceData(newData);
    setShowBulkActions(false);
  };

  const handleClearAll = () => {
    if (isViewOnly) return;
    const newData = {};
    students.forEach((student, index) => {
      const key = student.__attendanceKey || resolveStudentKey(student, `row_${index}`);
      newData[key] = { status: '' };
    });
    setAttendanceData(newData);
    setShowBulkActions(false);
  };

  const handleSubmit = async () => {
    if (isMarkMode) {
      const missingStatus = students.some((student, index) => {
        const key = student.__attendanceKey || resolveStudentKey(student, `row_${index}`);
        const status = attendanceData[key]?.status;
        return !status || status === '';
      });
      
      if (missingStatus) {
        setError('Please mark attendance for all students. Use "Mark All" buttons for quick selection.');
        return;
      }
    }
    
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      const records = students.map((student, index) => {
        const key = student.__attendanceKey || resolveStudentKey(student, `row_${index}`);
        const studentId =
          normalizeStudentId(student.studentId) ||
          normalizeStudentId(student.studentDetails?._id) ||
          normalizeStudentId(student.studentDetails?.studentId) ||
          key;

        return {
          studentId,
          status: attendanceData[key]?.status || 'absent'
        };
      });
      
      let response;
      if (isEditMode) {
        response = await api.put(`/api/faculty/attendance/session/${session._id}/edit`, { 
          records,
          facultyId,
          reason: 'Attendance edited by faculty'
        });
      } else {
        response = await api.post(`/api/faculty/attendance/session/${session._id}/mark`, { records, facultyId });
      }
      
      if (response.data.success) {
        const presentCount = records.filter(r => r.status === 'present').length;
        const percentage = Math.round((presentCount / students.length) * 100);
        setSuccess(`${presentCount}/${students.length} present (${percentage}%)`);
        if (onSuccess) onSuccess();
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.data.message || `Failed to ${isEditMode ? 'update' : 'mark'} attendance`);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'mark'} attendance`);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatistics = () => {
    let stats = { total: students.length, present: 0, absent: 0, pending: 0 };
    students.forEach((student, index) => {
      const key = student.__attendanceKey || resolveStudentKey(student, `row_${index}`);
      const status = attendanceData[key]?.status;
      if (status === 'present') stats.present++;
      else if (status === 'absent') stats.absent++;
      else stats.pending++;
    });
    return stats;
  };

  const statistics = getStatistics();

  const filteredStudents = students.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    const name = student.studentDetails?.name?.toLowerCase() || '';
    const usn = student.studentDetails?.USN?.toLowerCase() || '';
    const matchesSearch = name.includes(searchLower) || usn.includes(searchLower);
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'pending') {
      const key = student.__attendanceKey || resolveStudentKey(student);
      const status = attendanceData[key]?.status;
      return matchesSearch && (!status || status === '');
    }
    const key = student.__attendanceKey || resolveStudentKey(student);
    const currentStatus = attendanceData[key]?.status;
    return matchesSearch && currentStatus === filterStatus;
  });

  const formatTime12Hour = (timeStr) => {
    if (!timeStr) return '';
    const [hour, minute] = timeStr.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minute} ${ampm}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        
        {/* Compact Header */}
        <div className={`px-4 py-3 border-b rounded-t-xl ${
          isEditMode ? 'bg-blue-600' : isMarkMode ? 'bg-emerald-600' : 'bg-indigo-600'
        } text-white`}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">
                {isEditMode ? 'Edit Attendance' : isMarkMode ? 'Mark Attendance' : 'View Attendance'}
              </h2>
              {sessionDetails && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs text-white/90">
                  <div>
                    <span className="font-semibold">Class Name:</span><br/>
                    <span>{sessionDetails.className}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Subject:</span><br/>
                    <span>{sessionDetails.subjectSnapshot?.subjectName || sessionDetails.subjectId?.name}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Date:</span><br/>
                    <span>{new Date(sessionDetails.sessionDate).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Session Time:</span><br/>
                    <span>{formatTime12Hour(sessionDetails.startTime)} - {formatTime12Hour(sessionDetails.endTime)}</span>
                  </div>
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Compact Stats Bar */}
        <div className="px-4 py-2 bg-slate-50 border-b">
          <div className="flex gap-2 text-center">
            <div className="flex-1 bg-white rounded-lg p-1.5 shadow-sm">
              <div className="text-lg font-bold text-slate-800">{statistics.total}</div>
              <div className="text-[10px] text-slate-500">Total</div>
            </div>
            <button
              onClick={() => setFilterStatus(filterStatus === 'present' ? 'all' : 'present')}
              className={`flex-1 rounded-lg p-1.5 transition-all ${
                filterStatus === 'present' ? 'bg-green-600 text-white' : 'bg-white text-green-700 shadow-sm'
              }`}
            >
              <div className="text-lg font-bold">{statistics.present}</div>
              <div className="text-[10px]">Present</div>
            </button>
            <button
              onClick={() => setFilterStatus(filterStatus === 'absent' ? 'all' : 'absent')}
              className={`flex-1 rounded-lg p-1.5 transition-all ${
                filterStatus === 'absent' ? 'bg-red-600 text-white' : 'bg-white text-red-700 shadow-sm'
              }`}
            >
              <div className="text-lg font-bold">{statistics.absent}</div>
              <div className="text-[10px]">Absent</div>
            </button>
            <button
              onClick={() => setFilterStatus(filterStatus === 'pending' ? 'all' : 'pending')}
              className={`flex-1 rounded-lg p-1.5 transition-all ${
                filterStatus === 'pending' ? 'bg-yellow-600 text-white' : 'bg-white text-yellow-700 shadow-sm'
              }`}
            >
              <div className="text-lg font-bold">{statistics.pending}</div>
              <div className="text-[10px]">Pending</div>
            </button>
          </div>
        </div>

        {/* Action Bar */}
        {!isViewOnly && (
          <div className="px-4 py-2 border-b bg-white">
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium flex items-center gap-1"
              >
                <Users className="w-3.5 h-3.5" />
                Bulk
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showBulkActions ? 'rotate-180' : ''}`} />
              </button>
              
              {showBulkActions && (
                <div className="flex gap-1 ml-auto">
                  <button onClick={handleMarkAllPresent} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium">All Present</button>
                  <button onClick={handleMarkAllAbsent} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium">All Absent</button>
                  <button onClick={handleClearAll} className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-xs font-medium">Clear</button>
                </div>
              )}
              
              <div className="flex-1 relative ml-auto">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-32 sm:w-48 pl-7 pr-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Students List - Compact Table View */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : error ? (
            <div className="p-4 m-4 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          ) : (
            <>
              {success && (
                <div className="mx-4 mt-3 bg-green-50 text-green-700 p-2 rounded-lg text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {success}
                </div>
              )}
              
              {isViewOnly && (
                <div className="mx-4 mt-3 bg-blue-50 text-blue-700 p-2 rounded-lg text-xs">
                  Attendance is locked and cannot be edited.
                </div>
              )}
              
              {/* Compact Table */}
              <div className="divide-y divide-slate-100">
                {filteredStudents.map((student, idx) => {
                  const statusKey = student.__attendanceKey || resolveStudentKey(student, `row_${idx}`);
                  const currentStatus = attendanceData[statusKey]?.status || '';
                  const isPresent = currentStatus === 'present';
                  const isAbsent = currentStatus === 'absent';
                  const isPending = !currentStatus;
                  // Add serial number (slno) as idx + 1
                  return (
                    <div key={statusKey || idx} className={`px-3 py-2 flex items-center justify-between hover:bg-slate-50 ${
                      isPresent ? 'bg-green-50/30' : isAbsent ? 'bg-red-50/30' : ''
                    }`}>
                      {/* Serial Number */}
                      <div className="w-8 text-xs text-slate-500 font-semibold flex-shrink-0 text-center">
                        {idx + 1}
                      </div>
                      {/* Student Info - Compact */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold flex-shrink-0 overflow-hidden border border-indigo-200">
                          {(() => {
                            // Use backend URL from api.js for profile images
                            const backendUrl = api.defaults.baseURL || 'http://localhost:5000';
                            const profileUrl = student.studentDetails?.profilePhotoUrl || student.studentDetails?.profilePhoto?.url;
                            const name = student.studentDetails?.name || 'Student';
                            const initials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
                            // Show image if profileUrl exists and is not the default placeholder
                            const showImage = profileUrl && typeof profileUrl === 'string' && !profileUrl.endsWith('default-profile.png');
                            if (showImage) {
                              const imgSrc = profileUrl.startsWith('http') ? profileUrl : `${backendUrl}${profileUrl.startsWith('/') ? profileUrl : '/' + profileUrl}`;
                              return (
                                <img src={imgSrc} alt={name} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                              );
                            } else {
                              return <span>{initials}</span>;
                            }
                          })()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {student.studentDetails?.name || 'Unknown'}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate">
                            {student.studentDetails?.USN || student.studentDetails?.rollNumber || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Status Badge (small) */}
                      {isPending && !isViewOnly && (
                        <span className="text-[10px] text-yellow-600 bg-yellow-50 px-1.5 py-0.5 rounded hidden sm:inline-block">
                          Pending
                        </span>
                      )}
                      
                      {/* Present/Absent Buttons - Compact */}
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => handleStatusChange(student.studentId, 'present')}
                          disabled={isViewOnly}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                            isPresent
                              ? 'bg-green-600 text-white shadow-sm'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } ${isViewOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <UserCheck className="w-3 h-3" />
                          <span className="hidden sm:inline">P</span>
                        </button>
                        
                        <button
                          onClick={() => handleStatusChange(student.studentId, 'absent')}
                          disabled={isViewOnly}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                            isAbsent
                              ? 'bg-red-600 text-white shadow-sm'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          } ${isViewOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <UserX className="w-3 h-3" />
                          <span className="hidden sm:inline">A</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {filteredStudents.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No students found</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Compact Footer */}
        <div className="px-4 py-2 border-t bg-slate-50 rounded-b-xl flex justify-between items-center">
          <div className="text-[11px] text-slate-500">
            {filteredStudents.length}/{students.length} shown
            {!isViewOnly && statistics.pending > 0 && (
              <span className="ml-2 text-yellow-600">• {statistics.pending} pending</span>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-medium hover:bg-slate-100"
              disabled={submitting}
            >
              Cancel
            </button>
            
            {!isViewOnly && (
              <button
                onClick={handleSubmit}
                disabled={submitting || loading}
                className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {submitting ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;